import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_TONE,
  KYC_LABEL,
  MODE_LABEL,
  day,
  mad,
  today,
} from '../lib/format';
import { Avatar, Badge, EmptyState, ListingCard, Notice, Spinner } from '../components/ui';
import type { Booking, ColocationRequest, PropertyCard } from '../lib/types';
import { t } from '../lib/i18n';

/** The client's home: bookings, saved listings, colocation requests. */
export default function Account() {
  const { user, isHost } = useApp();
  const [tab, setTab] = useState<'bookings' | 'saved' | 'requests'>('bookings');
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [saved, setSaved] = useState<PropertyCard[] | null>(null);
  const [requests, setRequests] = useState<ColocationRequest[] | null>(null);

  useEffect(() => {
    void api
      .get<{ bookings: Booking[] }>('/bookings?role=guest')
      .then((response) => setBookings(response.bookings))
      .catch(() => setBookings([]));
    void api
      .get<{ items: PropertyCard[] }>('/saved-listings')
      .then((response) => setSaved(response.items))
      .catch(() => setSaved([]));
    void api
      .get<{ items: ColocationRequest[] }>('/colocation-requests/mine')
      .then((response) => setRequests(response.items))
      .catch(() => setRequests([]));
  }, []);

  const now = today();
  const upcoming = (bookings ?? []).filter((booking) => booking.endDate >= now);
  const past = (bookings ?? []).filter((booking) => booking.endDate < now);

  return (
    <div className="page page--mid stack stack--loose">
      <div className="row row--between row--wrap" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 14 }}>
          <Avatar name={user?.fullName ?? ''} url={user?.avatarUrl} size={54} />
          <div className="stack stack--tight">
            <h1 style={{ fontSize: 24 }}>{user?.fullName}</h1>
            <div className="row row--wrap" style={{ gap: 8 }}>
              <Badge tone={user?.kycStatus === 'APPROVED' ? 'badge--verified' : 'badge--warn'}>
                {KYC_LABEL[user?.kycStatus ?? 'NONE']}
              </Badge>
              {user?.emailVerified ? (
                <Badge tone="badge--ok">{t("Email vérifié")}</Badge>
              ) : (
                <Badge tone="badge--warn">{t("Email non vérifié")}</Badge>
              )}
              {user?.universityName ? <Badge>{user.universityName}</Badge> : null}
            </div>
          </div>
        </div>
        <div className="row row--wrap" style={{ gap: 8 }}>
          <Link className="btn btn--ghost btn--sm" to="/parametres">
            {t("Paramètres")}
          </Link>
          {user?.kycStatus !== 'APPROVED' ? (
            <Link className="btn btn--sm" to="/verification">
              {t("Vérifier mon identité")}
            </Link>
          ) : null}
        </div>
      </div>

      {user?.kycStatus !== 'APPROVED' ? (
        <Notice tone="warn">
          {t("La vérification d’identité rassure les hôtes et devient obligatoire pour publier une annonce. Comptez deux minutes.")}
        </Notice>
      ) : null}

      <div className="tabs" style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)' }}>
        {(
          [
            ['bookings', `${t('Réservations')}${bookings ? ` (${bookings.length})` : ''}`],
            ['saved', `${t('Enregistrées')}${saved ? ` (${saved.length})` : ''}`],
            ['requests', `${t('Mes demandes')}${requests ? ` (${requests.length})` : ''}`],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`tab ${tab === value ? 'tab--on' : ''}`}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'bookings' ? (
        bookings === null ? (
          <Spinner label={t("Chargement…")} />
        ) : bookings.length === 0 ? (
          <EmptyState
            title={t("Aucune réservation")}
            description={t("Vos prochaines réservations apparaîtront ici.")}
            action={
              <Link className="btn" to="/recherche">
                {t("Explorer les logements")}
              </Link>
            }
          />
        ) : (
          <div className="stack stack--loose">
            {upcoming.length > 0 ? (
              <section className="stack">
                <h2>{t("À venir")}</h2>
                {upcoming.map((booking) => (
                  <BookingRow key={booking.id} booking={booking} />
                ))}
              </section>
            ) : null}
            {past.length > 0 ? (
              <section className="stack">
                <h2>{t("Passées")}</h2>
                {past.map((booking) => (
                  <BookingRow key={booking.id} booking={booking} />
                ))}
              </section>
            ) : null}
          </div>
        )
      ) : null}

      {tab === 'saved' ? (
        saved === null ? (
          <Spinner label={t("Chargement…")} />
        ) : saved.length === 0 ? (
          <EmptyState
            title={t("Aucune annonce enregistrée")}
            description={t("Touchez le cœur sur une annonce pour la retrouver ici.")}
            action={
              <Link className="btn" to="/recherche">
                Explorer
              </Link>
            }
          />
        ) : (
          <div className="grid">
            {saved.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )
      ) : null}

      {tab === 'requests' ? (
        requests === null ? (
          <Spinner label={t("Chargement…")} />
        ) : requests.length === 0 ? (
          <EmptyState
            title={t("Aucune demande de colocation")}
            description={t("Publiez une demande pour que les hôtes et les colocataires vous trouvent.")}
            action={
              <Link className="btn" to="/colocations/publier">
                {t("Publier une demande")}
              </Link>
            }
          />
        ) : (
          <div className="stack">
            {requests.map((request) => (
              <div key={request.id} className="card card--pad row row--between row--wrap" style={{ gap: 12 }}>
                <div className="stack stack--tight">
                  <div className="row" style={{ gap: 8 }}>
                    <span className="strong">
                      {t(request.city)}
                      {request.neighborhood ? ` · ${request.neighborhood}` : ''}
                    </span>
                    <Badge tone={request.status === 'ACTIVE' ? 'badge--ok' : ''}>{request.status}</Badge>
                    {request.visibility === 'PRIVATE' ? <Badge>{t("Privée")}</Badge> : null}
                  </div>
                  <span className="small muted">
                    {mad(request.budget.minCentimes)} – {mad(request.budget.maxCentimes)}/mois ·
                    emménagement {day(request.moveInDate, true)} · {request.durationMonths} {t("mois ·")}{' '}
                    {request.flatmatesWanted} {t("coloc recherché(s)")}
                  </span>
                </div>
                <Link className="btn btn--ghost btn--sm" to={`/colocations/${request.id}/modifier`}>
                  {t("Modifier")}
                </Link>
              </div>
            ))}
            <Link className="btn btn--ghost" to="/colocations/publier">
              {t("+ Publier une nouvelle demande")}
            </Link>
          </div>
        )
      ) : null}

      {!isHost ? (
        <section className="card card--pad row row--between row--wrap" style={{ gap: 14 }}>
          <div className="stack stack--tight">
            <span className="strong">{t("Vous avez une chambre libre ?")}</span>
            <span className="small muted">{t("Devenez hôte en trois étapes. Publication gratuite.")}</span>
          </div>
          <Link className="btn" to="/devenir-hote">
            {t("Devenir hôte")}
          </Link>
        </section>
      ) : null}
    </div>
  );
}

function BookingRow({ booking }: { booking: Booking }) {
  return (
    <Link
      to={`/reservations/${booking.id}`}
      className="card card--pad row row--between row--wrap"
      style={{ gap: 12, color: 'inherit' }}
    >
      <div className="stack stack--tight">
        <div className="row row--wrap" style={{ gap: 8 }}>
          <span className="strong">{booking.property.title}</span>
          <Badge tone={BOOKING_STATUS_TONE[booking.status]}>{BOOKING_STATUS_LABEL[booking.status]}</Badge>
        </div>
        <span className="small muted">
          {MODE_LABEL[booking.mode]} · {day(booking.startDate, true)} → {day(booking.endDate, true)} ·{' '}
          {t(booking.property.city)}
        </span>
        <span className="tiny muted">{t("Réf.")} {booking.reference}</span>
      </div>
      <div className="stack stack--tight" style={{ alignItems: 'flex-end' }}>
        <span className="strong">{mad(booking.amounts.platformFeeCentimes)}</span>
        <span className="tiny muted">{t("frais en ligne")}</span>
        <span className="tiny muted">
          + {mad(booking.amounts.hostTotalCentimes)} {t("en espèces")}
        </span>
      </div>
    </Link>
  );
}
